import { useContext } from 'react';
import { styled } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from 'yup';
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import apiCallFormData from "../../../../utils/apiFormDataCall";
import { NotificationContext } from '../../../../providers/notification';

interface ComponentProps {
    handleClose: () => void;
}

const CreateApprovalType: React.FC<ComponentProps> = ({ handleClose }) => {
    const { showNotification } = useContext(NotificationContext);
    const queryClient = useQueryClient();

    const postApprovalType = (data: FormData) => {
        return apiCallFormData().post('/ApprovalType/Create', data);
    };

    const postApprovalTypeMutation = useMutation(['post-approval-type'], postApprovalType, {retry: 0});

    const postApprovalTypeHandler = async (values: FormikValues, resetForm: () => void) => {
        const formData = new FormData();
        formData.append('ApprovalTypId', values?.id);
        formData.append('ApprovalTypName', values?.name);

        try {
            await postApprovalTypeMutation.mutateAsync(formData, {
                onSuccess: () => {
                    showNotification({
                        message: 'Approval type successfully added',
                        type: 'success',
                    });

                    queryClient.refetchQueries(['get-approval-types']);
                    resetForm();
                    handleClose();
                }
            })
        } catch (error: any){
            showNotification({
                message: error?.response?.data?.title || error?.message,
                type: 'error',
            });
        }
    };

    const validationSchema = Yup.object().shape({
        id: Yup.number().typeError('Only numbers are allowed').required('Required'),
        name: Yup.string().matches(/^[A-Za-z\s!@#$%^&*()/]+$/, 'Only text is allowed').required('Approval type name is required'),
    });

    const formik = useFormik<FormikValues>({
        initialValues: {
            id:'',
            name: ''
        },
        onSubmit: (values, {resetForm}) => {
            postApprovalTypeHandler(values, resetForm);
        },
        validationSchema,
    });

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='id'
                        type='text'
                        as={Input}
                        label='Approval Type Id'
                        placeholder='Id'
                    />
                    <Field 
                        name='name'
                        type='text'
                        as={Input}
                        label='Approval Type Name'
                        placeholder='Name'
                    />
                    {/* <Field 
                        name='status'
                        type='text'
                        as={Select}
                        label='Status'
                    /> */}
                </FormWrapper>
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton onClick={handleClose} text='Cancel'/>
                        <StyledButton 
                            type="submit" 
                            text={postApprovalTypeMutation.isLoading ? 'Submitting...' : 'Submit'}
                            disabled={postApprovalTypeMutation.isLoading}
                        />
                    </ButtonContainer>
                </ButtonWrapper>
            </form>
        </FormikProvider>
    )
};

export default CreateApprovalType;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

